import React, { useMemo } from 'react';
import styled from 'styled-components';
import OrganizerModuleBlock from './OrganizerModuleBlock';
import { OrganizerHorizontalDropZone } from './OrganizerDropZone';
import { canRowAcceptModule } from '../../utils/layoutUtils';

// Row container
const RowContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.2s ease;
`;

// Grid for modules within a row
const ModulesGrid = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: stretch;
`;

/**
 * OrganizerRow - A single row in the layout organizer
 */
const OrganizerRow = ({ row, onDrop, isDragging, draggedModule, themeColor, recentlyDroppedId }) => {
  const modules = row.pageModules || [];
  const moduleCount = modules.length;

  // Determine which drop zones to show
  const dropZones = useMemo(() => {
    if (!isDragging || !draggedModule) {
      return { left: false, center: false, right: false };
    }

    const draggedModuleId = draggedModule.moduleId;
    const canAccept = canRowAcceptModule(row, draggedModuleId);
    const isModuleInThisRow = modules.some((m) => m.id === draggedModuleId);

    if (!canAccept && !isModuleInThisRow) {
      return { left: false, center: false, right: false };
    }

    if (moduleCount === 0) {
      return { left: true, center: false, right: false };
    }

    if (moduleCount === 1) {
      if (isModuleInThisRow) {
        return { left: false, center: false, right: false };
      }
      return { left: true, center: false, right: true };
    }

    if (moduleCount === 2) {
      if (isModuleInThisRow) {
        return { left: true, center: false, right: true };
      }
      return { left: true, center: true, right: true };
    }

    if (moduleCount === 3) {
      if (isModuleInThisRow) {
        return { left: true, center: true, right: true };
      }
      return { left: false, center: false, right: false };
    }

    return { left: false, center: false, right: false };
  }, [isDragging, draggedModule, modules, moduleCount, row]);

  return (
    <RowContainer>
      <ModulesGrid>
        <OrganizerHorizontalDropZone position='left' rowId={row.id} onDrop={onDrop} isVisible={dropZones.left} />

        {modules.map((module, index) => (
          <React.Fragment key={module.id}>
            <OrganizerModuleBlock module={module} themeColor={themeColor} isRecentlyDropped={module.id === recentlyDroppedId} />
            {index === 0 && moduleCount >= 2 && (
              <OrganizerHorizontalDropZone position='center' rowId={row.id} onDrop={onDrop} isVisible={dropZones.center} />
            )}
          </React.Fragment>
        ))}

        <OrganizerHorizontalDropZone position='right' rowId={row.id} onDrop={onDrop} isVisible={dropZones.right} />
      </ModulesGrid>
    </RowContainer>
  );
};

export default OrganizerRow;
